// stores/favorites.js — 收藏夹 store(跨设备同步)
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const STORAGE_KEY = 'fucai3d_max_fav';
const MAX_LOCAL = 50;

export const useFavoritesStore = create(
  persist(
    (set, get) => ({
      favorites: [], // 本地: [{id?, title, codes, count, level, source, time, _synced?}]
      serverFavs: [], // 服务端
      lastSyncedAt: null,
      syncing: false,

      // 添加
      add: (fav) => {
        const f = {
          ...fav,
          time: fav.time || new Date().toISOString(),
          _synced: false, // 标记未同步
        };
        set(state => {
          // 去重(同 title + codes)
          const exists = state.favorites.find(
            x => x.title === f.title && x.codes === f.codes
          );
          if (exists) return state;
          return { favorites: [f, ...state.favorites].slice(0, MAX_LOCAL) };
        });
        // 触发后台同步
        get().syncToServer();
      },

      // 删除(本地)
      remove: (idx) => {
        set(state => ({
          favorites: state.favorites.filter((_, i) => i !== idx)
        }));
      },

      // 清空本地
      clearLocal: () => set({ favorites: [] }),

      // 从服务器拉取
      fetchFromServer: async () => {
        try {
          const r = await fetch(`${import.meta.env.VITE_API_BASE || 'https://3d-max-api.mz18607358885.workers.dev'}/api/favorites`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('fucai3d_token') || ''}` },
          });
          const data = await r.json();
          if (data.ok) {
            set({
              serverFavs: data.favorites || [],
              lastSyncedAt: Date.now(),
            });
            // merge 到本地
            get().mergeServerToLocal();
            return data.favorites || [];
          }
          return [];
        } catch (e) {
          console.warn('[favorites] fetch failed:', e);
          return [];
        }
      },

      // 合并服务器到本地
      mergeServerToLocal: () => {
        const server = get().serverFavs;
        if (!server.length) return;
        set(state => {
          const merged = [...state.favorites];
          for (const sf of server) {
            const exists = merged.find(
              x => x.title === sf.title && x.codes === sf.codes
            );
            if (!exists) {
              merged.push({
                id: sf.id,
                title: sf.title,
                codes: sf.codes,
                count: sf.count,
                level: sf.level,
                source: sf.source,
                time: new Date(sf.created_at * 1000).toISOString(),
                _synced: true,
              });
            } else if (!exists.id) {
              exists.id = sf.id;
              exists._synced = true;
            }
          }
          return { favorites: merged.slice(0, MAX_LOCAL * 2) };
        });
      },

      // 同步本地到服务器
      syncToServer: async () => {
        const state = get();
        if (state.syncing) return;
        const unsynced = state.favorites.filter(f => !f._synced && !f.id);
        if (unsynced.length === 0) return;
        set({ syncing: true });
        try {
          const r = await fetch(`${import.meta.env.VITE_API_BASE || 'https://3d-max-api.mz18607358885.workers.dev'}/api/favorites/sync`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('fucai3d_token') || ''}`,
            },
            body: JSON.stringify({ favorites: unsynced }),
          });
          const data = await r.json();
          if (data.ok) {
            // 标记已同步
            set(state => ({
              favorites: state.favorites.map(f =>
                unsynced.find(u => u.title === f.title && u.codes === f.codes)
                  ? { ...f, _synced: true }
                  : f
              ),
              lastSyncedAt: Date.now(),
            }));
            // 拉一次最新
            get().fetchFromServer();
          }
        } catch (e) {
          console.warn('[favorites] sync failed:', e);
        } finally {
          set({ syncing: false });
        }
      },

      // 单个删除(服务器 + 本地)
      removeById: async (id) => {
        set(state => ({
          favorites: state.favorites.filter(f => f.id !== id),
          serverFavs: state.serverFavs.filter(f => f.id !== id),
        }));
        try {
          await fetch(`${import.meta.env.VITE_API_BASE || 'https://3d-max-api.mz18607358885.workers.dev'}/api/favorites/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('fucai3d_token') || ''}` },
          });
        } catch (e) {}
      },

      // 清空所有(服务器 + 本地)
      clearAll: async () => {
        set({ favorites: [], serverFavs: [] });
        try {
          await fetch(`${import.meta.env.VITE_API_BASE || 'https://3d-max-api.mz18607358885.workers.dev'}/api/favorites/clear`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('fucai3d_token') || ''}` },
          });
        } catch (e) {}
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ favorites: state.favorites }),
    }
  )
);
